import { type Address, type Chain, type Transport, type Account } from 'viem';
import { type PublicClient, type WalletClient } from './doc-types.js';
import { type ClientConfig } from './types.js';

export abstract class BaseClient {
    public client: WalletClient;
    public publicClient?: PublicClient;
    protected registryAddress?: Address;
    protected gTokenAddress?: Address;
    protected gTokenStakingAddress?: Address;
    protected paymasterFactoryAddress?: Address;
    protected entryPointAddress?: Address;

    constructor(config: ClientConfig) {
        if (!config.client) {
            throw new Error('WalletClient is required for Business Clients');
        }
        if (!config.client.account) {
            throw new Error('WalletClient must have an account attached');
        }

        this.client = config.client;
        this.publicClient = config.publicClient;
        this.registryAddress = config.registryAddress;
        this.gTokenAddress = config.gTokenAddress;
        this.gTokenStakingAddress = config.gTokenStakingAddress;
        this.paymasterFactoryAddress = config.paymasterFactoryAddress;
        this.entryPointAddress = config.entryPointAddress;
    }

    /**
     * Get the account address of the connected wallet
     */
    public getAddress(): Address {
        return this.client.account!.address;
    }

    /**
     * Helper to ensure public client exists or fallback to wallet client (if it supports read)
     */
    public getStartPublicClient(): PublicClient | WalletClient {
        return this.publicClient || this.client;
    }

    protected requireRegistry(): Address {
        if (!this.registryAddress) {
            throw new Error('Registry address is not configured for this client');
        }
        return this.registryAddress;
    }

    protected requireGToken(): Address {
        if (!this.gTokenAddress) {
            throw new Error('GToken address is not configured for this client');
        }
        return this.gTokenAddress;
    }

    protected requireGTokenStaking(): Address {
        if (!this.gTokenStakingAddress) {
            throw new Error('GTokenStaking address is not configured for this client');
        }
        return this.gTokenStakingAddress;
    }

    protected requirePaymasterFactory(): Address {
        if (!this.paymasterFactoryAddress) {
            throw new Error('PaymasterFactory address is not configured for this client');
        }
        return this.paymasterFactoryAddress;
    }

    protected requireEntryPoint(): Address {
        if (!this.entryPointAddress) {
            throw new Error('EntryPoint address is not configured for this client');
        }
        return this.entryPointAddress;
    }
}
